import {
  type Ref,
  type RefCallback,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react'

/**
 * A writing box that is as tall as what is written in it.
 *
 * The height is taken from the contents on mount, on every commit, on every
 * keystroke and whenever the box changes width, so it tracks the text in both
 * directions — a deleted paragraph gives its room back rather than leaving a
 * hole somebody has to drag shut.
 *
 * It is measured rather than counted. A line count cannot be derived from the
 * text: what wraps depends on the width the box was given and on the face it is
 * set in, and both change under the same string. Width is therefore a trigger
 * in its own right, and it moves without React — a band dragged narrower, a
 * window resized, a panel opening beside it. Observing the element covers all
 * three, because each of them ends at the same fact.
 *
 * The reset before the reading is the whole mechanism: `scrollHeight` never
 * reports less than the height already standing, so a box measured without it
 * can only ever grow. Borders are added back because `scrollHeight` stops at
 * the padding box while a border-box height counts them.
 *
 * The caller's own ref is merged in, since a component takes one as an ordinary
 * prop and the element has exactly one to give.
 */
export function useAutoGrow(
  /** Off leaves the element alone — a box sized by its `rows` sets its own. */
  enabled: boolean,
  /** The ref the caller passed, which still has to reach the element. */
  forwarded?: Ref<HTMLTextAreaElement>,
): RefCallback<HTMLTextAreaElement> {
  const own = useRef<HTMLTextAreaElement | null>(null)
  const watching = useRef<ResizeObserver | null>(null)
  const width = useRef<number | null>(null)

  const fit = useCallback(() => {
    const el = own.current
    if (!el) return
    if (!enabled) {
      /* Off means the element sizes itself again. The height written while it
         was on is cleared, because an inline height beats the `rows` that is
         now supposed to decide. */
      el.style.height = ''
      return
    }
    el.style.height = 'auto'
    const borders = el.offsetHeight - el.clientHeight
    el.style.height = `${el.scrollHeight + borders}px`
  }, [enabled])

  /*
   * Every commit, before paint. A commit is what changes the text, so it is the
   * trigger rather than any value: the height is corrected in the same frame
   * the words arrived, and no frame is shown at the height the last ones needed.
   */
  useLayoutEffect(fit)

  return useCallback(
    (node: HTMLTextAreaElement | null) => {
      const previous = own.current
      if (previous) previous.removeEventListener('input', fit)
      watching.current?.disconnect()
      watching.current = null
      width.current = null

      own.current = node
      if (typeof forwarded === 'function') forwarded(node)
      else if (forwarded) forwarded.current = node

      if (node) {
        /* The element's own event, so the box follows the typing whether or not
           the value is held in state. A commit is not guaranteed by a keystroke
           — an uncontrolled box re-renders for nothing — and a box that grew
           only for controlled callers would be a prop doc nobody could read off
           the behavior. */
        node.addEventListener('input', fit)
        /* Only a change of WIDTH is a reason to measure again. Fitting sets the
           height, which the observer would otherwise report back as a change
           and measure for a second time, every time. */
        watching.current = new ResizeObserver((entries) => {
          const now = entries[0]?.contentRect.width ?? null
          if (now === width.current) return
          width.current = now
          fit()
        })
        watching.current.observe(node)
      }

      fit()
    },
    [fit, forwarded],
  )
}
